/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'./EventEmitter',
	'underscore'
], (EventEmitter, _) => {
	class APIEventSupport {

		constructor(apiImplementation, eventSpec, apiName, features) {
			this.apiImplementation = apiImplementation;
			this.eventSpec = this.createSpec(eventSpec);
			this.apiName = apiName;
			const options = this.getEventEmitterOptions();
			const transaction = features && features['Dashboard.Transaction'];
			this.eventEmitter = new EventEmitter(options, transaction);

			// Extend the instance with event support functionality
			this.apiImplementation.getEventSupport = () => {
				return this;
			};
			this.apiImplementation.on = this.on.bind(this);
			this.apiImplementation.off = this.off.bind(this);

			if (this.eventSpec.serializeActions) {
				this.apiImplementation.serializeActions = this.serializeActions.bind(this);
			}
		}

		isSupported(functionName){
			return !!this.getActionSpec(functionName);
		}

		extendAPI(apiInstance, apiInterface) {
			this.apiInstance = apiInstance;

			// If we only do expose on/off in the API interface, enable event emitter trigger on self
			// NOTE: apiInstance can implement multiple apiInterfaces
			// it is not needed and better for performance
			if (apiInterface.prototype.on) {
				this.eventEmitter.enableTriggerOnSelf();
			}
		}

		serializeActions() {
			// Once we need to support complicated serializeActions, then
			// we can have a serializeActions function provided by the eventSpec.
			// But for now, we compute the state using the event spec getUndoActions
			// The state should produce a map with keys that can be resolves to a function call in the API
			// and the value of the key is the parameters array to be passed to the function
			const serializedActions = [];
			const actions = this.eventSpec.actions;
			const addedActions = {};
			if (actions) {
				let name;
				for (name in actions) {
					if (actions[name].isResolvePostResponse) {
						// the current logic of serializeActions does not support isResolvePostResponse = true
						throw new Error('serializeActions is not supported when isResolvePostResponse = true for ' + name);
					}

					const undoActions = this.getUndoActions(actions[name], name);
					if (undoActions.length !== 1) {
						// the serializeActions cannot be computed when we have more than one undoAction.
						throw new Error('serializeActions is not supported when undoActions length is not 1');
					}

					if (Array.isArray(undoActions[0].params)) {
						let isAllNull = true;
						undoActions[0].params.forEach((value) => {
							if (value !== undefined && value !== null) {
								isAllNull = false;
							}
						});
						if (!isAllNull) {
							const actionAsString = JSON.stringify(undoActions[0]);
							if (!addedActions[actionAsString]){
								addedActions[actionAsString] = true;
								serializedActions.push(undoActions[0]);
							}
						}
					}
				}
			}

			// serialize any sub object
			if (this.eventSpec.serializeActions.children) {
				this.eventSpec.serializeActions.children.forEach(child => {
					if(typeof(child.getObject) === 'function'){
						const obj = child.getObject();
						if (obj && typeof (obj.serializeActions) === 'function'){
							const childSerializeActions = obj.serializeActions();
							childSerializeActions.forEach(action => {
								action.callStack = child.callStack;
							});
							serializedActions.push(... childSerializeActions);
						}
					}
				});
			}

			return serializedActions;
		}

		/**
		 * Complete the given event information provided in the api spec with the missing details. Missing details will use the default behavior.
		 * For example:
		 *  - default getAction()  - return all the parameters in the arguments except the last parameter if it contains a transactinId
		 *  - default getInfo() - return an object with a "value" parameter that contains all the action arguments except the last parameter if it contains a transaction id
		 *  - default getTransactionInfo() - Assume that the last parameter is the transactionInfo if it contains a transactionId
		 *  - default onBeforeAction() - default function created in case it is not added in the APISpec and autoCreateTransaction is true. Starts a transaction.
		 *  - default onAfterAction() - default function created in case it is not added in the APISpec and autoCreateTransaction is true. Ends the transaction created by onBeforeAction.
		 *
		 * @param {*} spec
		 */
		createSpec(eventSpec = {}) {
			const actions = eventSpec.actions;
			let name;
			for (name in actions) {
				const actionName = name;
				const transactionParamIndex = actions[name].transactionParamIndex;

				if (!actions[name].getUndoActions) {
					actions[name].getUndoActions = () => {
						return [{
							name: actionName,
							params: []
						}];
					};
				}

				if (actions[name].autoCreateTransaction && !actions[name].onBeforeAction && !actions[name].onAfterAction && actions[name].transactionAPI) {
					const transactionAPI = actions[name].transactionAPI;
					actions[name].onBeforeAction = (args) => {
						const options = {};
						if (!args[transactionParamIndex]) {
							args[transactionParamIndex] = transactionAPI.startTransaction();
							options.transactionCreated = true;
						}
						return options;
					};
					actions[name].onAfterAction = (args, returnValue, options) => {
						if (options.transactionCreated) {
							transactionAPI.endTransaction(args[transactionParamIndex]);
						}
					};
				}

				if (!actions[name].getAction) {
					actions[name].getAction = (args) => {
						const params = args.filter((param, index) => {
							return !(index === args.length - 1 && this._isTransactionToken(param));
						});
						return {
							name: actionName,
							params: params
						};
					};
				}

				if (!actions[name].getInfo) {
					actions[name].getInfo = (args) => {
						const values = args.filter((param, index) => {
							return !(index === args.length - 1 && (index === transactionParamIndex || this._isTransactionToken(param)));
						});
						const info = {
						};
						if (values.length === 1) {
							info.value = values[0];
						} else if (values.length > 1) {
							info.value = values;
						}
						return info;
					};
				}

				if (!actions[name].getTransactionToken) {
					actions[name].getTransactionToken = ((action) => (args) => {
						let token = null;
						if (args.length > 0) {
							const tokenParamIndex = action.transactionParamIndex === undefined ? args.length - 1 : action.transactionParamIndex;
							const tokenParam = args[tokenParamIndex];
							if (this._isTransactionToken(tokenParam)) {
								token = tokenParam;
							}
						}
						return token;
					})(actions[name]);
				}
			}
			return eventSpec;
		}

		_isTransactionToken(param) {
			return param && (param.transactionId || param.__transaction);
		}

		getUndoActions(functionEventSpec, actionName, args, returnValue) {
			const undoActions = functionEventSpec.getUndoActions(args, returnValue);
			undoActions.forEach((element) => {
				// if not explicit reverse action name specified, we will default to the action name
				if (!element.name) {
					element.name = actionName;
				}
			});
			return undoActions;
		}

		onBeforeAction(actionName, args, apiContext) {
			const onBeforeActionResult = {};
			const functionEventSpec = this.getActionSpec(actionName);

			if (functionEventSpec && apiContext && apiContext.transactionToken) {
				const index = functionEventSpec.transactionParamIndex !== undefined ? functionEventSpec.transactionParamIndex: args.length;
				args[index] = apiContext.transactionToken;
			}

			if (functionEventSpec && typeof (functionEventSpec.onBeforeAction) === 'function') {
				onBeforeActionResult.options = functionEventSpec.onBeforeAction(args);
			}

			if (functionEventSpec && typeof (functionEventSpec.getUndoActions) === 'function' && !functionEventSpec.isResolvePostResponse) {
				onBeforeActionResult.undoActions = this.getUndoActions(functionEventSpec, actionName, args);
			}

			return onBeforeActionResult;

		}

		onAfterAction(actionName, args, returnValue, onBeforeAction, apiContext) {
			const functionEventSpec = this.getActionSpec(actionName);

			if (functionEventSpec &&
				typeof (functionEventSpec.getAction) === 'function' &&
				typeof (functionEventSpec.getInfo) === 'function' &&
				typeof (functionEventSpec.getTransactionToken) === 'function' &&
				typeof (functionEventSpec.getUndoActions) === 'function') {

				const action = functionEventSpec.getAction(args, returnValue);
				// action name should always match.
				action.name = actionName;

				if (functionEventSpec.isResolvePostResponse) {
					onBeforeAction.undoActions = this.getUndoActions(functionEventSpec, actionName, args, returnValue);
				}

				// AN event spec can decide when to trigger an event or not by providing a function isTriggerEvent
				const triggerEventFunction = functionEventSpec.isTriggerEvent || ((action, undoActions/*, returnValue*/) => {
					// compare the current argument with the current,
					// If they are the same, we will avoid triggering an event
					// 1 - Always trigger if we have more than one reverse action
					let isTriggerEvent = undoActions.length > 1;
					if (!isTriggerEvent){
						const undoAction = undoActions[0] || {};
						// 2 - Always trigger if the action name is not the same as the reverse action
						isTriggerEvent = action.name !== undoAction.name;
						if (!isTriggerEvent){
							// 3 - Compare the action params in the action and the reverse action
							const actionArgs = action.params ? JSON.stringify(action.params) : '[]';
							const reverseArgs = undoAction.params ? JSON.stringify(undoAction.params) : 'null';
							isTriggerEvent = actionArgs !== reverseArgs;
						}
					}
					return isTriggerEvent;
				});

				const isTriggerEvent = triggerEventFunction(action, onBeforeAction.undoActions, returnValue);

				if (isTriggerEvent) {
					const info = functionEventSpec.getInfo(args, returnValue, onBeforeAction);
					if (functionEventSpec.info) {
						_.extend(info, functionEventSpec.info);
					}
					// Add a default action name to the payload info if it was not explicitly specified
					if (!info.actionName){
						info.actionName = actionName;
					}

					let transactionInfo = functionEventSpec.getTransactionToken(args) || { __transaction: true };

					// clone it so that original object is not updated with event hints
					transactionInfo = JSON.parse(JSON.stringify(transactionInfo));
					if (transactionInfo.source === 'event'){
						transactionInfo.eventGenerated = true;
					} else {
						transactionInfo.source = 'event';
					}

					let eventName;
					if( typeof(functionEventSpec.getEventName) === 'function'){
						eventName = functionEventSpec.getEventName(args);
					} else {
						eventName = functionEventSpec.eventName;
					}
					const event = {
						type: functionEventSpec.type || 'change',
						name: eventName,
						info: info,
						tracking: {
							action: action,
							undoActions: onBeforeAction.undoActions
						},
						transactionToken: transactionInfo,
						context: apiContext ? apiContext.toJSON(): undefined
					};
					if (apiContext){
						apiContext.addEvent(event);
					}

					this.triggerEvent(event);
				}

				if (functionEventSpec && typeof (functionEventSpec.onAfterAction) === 'function') {
					functionEventSpec.onAfterAction(args, returnValue, onBeforeAction.options);
				}

			}
		}

		getActionSpec(name) {
			return this.eventSpec.actions && this.eventSpec.actions[name];
		}

		/**
		 * Enable the the event propagation in a given child API object
		 *
		 * @param {Object} childAPI - child api where we want to enable event propagation
		 * @param {Object} propagationInfo
		 * @param {String} [propagationInfo.parentEventEmitter] Event namespace. The namespace will be appended to the  event name when the event is propagated
		 * @param {String} [propagationInfo.namespace] Event namespace. The namespace will be appended to the  event name when the event is propagated
		 * @param {Object} [propagationInfo.info] Object that contains information that will be added to the event info when the event is propagation
		 * @param {Object} [propagationInfo.callStack] callback callStack info. Contains info on the method and parameter that can be used to reach the API that originated the event
		 * @param {String} [propagationInfo.callStack.name] function name of the parent API that can be used to retreive the child api
		 * @param {Object[]} [propagationInfo.callStack.params] params used with the function name to retreive the child api
		 *
		 */
		enableChildEventPropagation(child, propagationInfo = {}) {
			if (typeof (child.getEventSupport) === 'function') {
				const childEventSupport = child.getEventSupport();
				propagationInfo.parentEventEmitter = this.eventEmitter;
				childEventSupport.enableEventPropagation(propagationInfo);
			}
		}

		enableEventPropagation(propagationInfo) {
			this.propagationInfo = propagationInfo;
			if (this.eventEmitter) {
				this.eventEmitter.enableEventPropagation(this.getEventEmitterOptions());
			}
		}

		getEventEmitterOptions() {

			// clone the eventSpec propagation info so that we don't modify it
			const options = this.eventSpec.propagationInfo ? JSON.parse(JSON.stringify(this.eventSpec.propagationInfo)) : {};
			// Deep extend the eventSpec propagation using using this.propagationInfo
			if (this.propagationInfo){
				let name;
				for (name in this.propagationInfo){
					if(options[name]){
						_.extend(options[name], this.propagationInfo[name]);
					} else {
						options[name] = this.propagationInfo[name];
					}
				}
			}
			options.name = this.apiName;
			return options;
		}

		triggerEvent(event) {
			if (this.eventEmitter) {
				this.eventEmitter.trigger(event);
			}
		}

		on(name, handler, context) {
			if (this.eventEmitter) {
				return this.eventEmitter.on(name, handler, context);
			}
		}

		off(name, handler, context) {
			if (this.eventEmitter) {
				this.eventEmitter.off(name, handler, context);
			}
		}

	}

	return APIEventSupport;
});
